/**
 * ForecastBuckets — Custom SVG forecast bucket composition chart.
 *
 * Canon §6: the bucket label is plain text. The value bar carries the canon
 * score-band color for that band. Order is sourced from forecast-buckets.ts.
 * Do NOT redefine bucket logic here.
 */

import React from "react";
import { BUCKET_ORDER, SCORE_BAND_COLORS } from "@/lib/forecast-buckets";

interface BucketData {
  count: number;
  totalValue: number;
}

interface ForecastBucketsProps {
  buckets: Record<string, BucketData>;
  totalPipeline: number;
}

function formatCurrency(v: number): string {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
}

export function ForecastBuckets({ buckets, totalPipeline }: ForecastBucketsProps) {
  const maxValue = Math.max(
    ...BUCKET_ORDER.map((b) => buckets[b]?.totalValue ?? 0),
    1,
  );

  return (
    <div className="w-full flex flex-col gap-3">
      <div className="flex items-baseline gap-2">
        <span className="text-[13px] font-medium text-muted-foreground">Total pipeline</span>
        <span className="text-[20px] font-bold tabular-nums text-[#16A34A]">
          {formatCurrency(totalPipeline)}
        </span>
      </div>
      <svg
        viewBox={`0 0 400 ${BUCKET_ORDER.length * 44}`}
        className="w-full"
        preserveAspectRatio="none"
        aria-label="Forecast bucket composition"
        role="img"
      >
        {BUCKET_ORDER.map((key, i) => {
          const color = SCORE_BAND_COLORS[key];
          const data = buckets[key];
          const rawValue = data?.totalValue ?? 0;
          const count = data?.count ?? 0;
          const barWidth = maxValue > 0 ? (rawValue / maxValue) * 300 : 0;
          const y = i * 44;
          return (
            <g key={key} transform={`translate(0, ${y})`}>
              <text x="0" y="14" fontSize="11" fontWeight="600" fill="#E5E7EB" fontFamily="inherit">
                {key}
              </text>
              <rect x="0" y="20" width="300" height="16" rx="3" fill="#1F2937" />
              {barWidth > 0 && (
                <rect
                  x="0"
                  y="20"
                  width={barWidth}
                  height="16"
                  rx="3"
                  fill={color}
                  opacity={key === "Omit" ? 0.7 : 1}
                />
              )}
              <text
                x="308"
                y="31"
                fontSize="11"
                fontWeight="600"
                fill={rawValue > 0 ? "#E5E7EB" : "#6B7280"}
                fontFamily="inherit"
              >
                {formatCurrency(rawValue)}
              </text>
              <text x="360" y="31" fontSize="10" fill="#6B7280" fontFamily="inherit">
                ({count})
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
